import { Box, Paper, Stepper, Step, StepLabel, Button, LinearProgress, Typography, Grid } from '@mui/material';
import { ArrowBack, Save, ArrowForward, Check } from '@mui/icons-material';

interface WizardLayoutProps {
  steps: { id: string; title: string }[];
  currentStepIndex: number;
  progress: number;
  isSaving: boolean;
  isCompleted: boolean;
  isFirstStep: boolean;
  isLastStep: boolean;
  onBack?: () => void;
  onSaveDraft?: () => void;
  onNext?: () => void;
  onComplete?: () => void;
  onDone?: () => void;
  onReset?: () => void;
  footerActions?: { label: string; to?: string; onClick?: () => void }[];
  children?: React.ReactNode;
}

export const WizardLayout = ({
  steps,
  currentStepIndex,
  progress,
  isSaving,
  isCompleted,
  isFirstStep,
  isLastStep,
  onBack,
  onSaveDraft,
  onNext,
  onComplete,
  onDone,
  onReset,
  footerActions,
  children,
}: WizardLayoutProps) => {
  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
      <LinearProgress variant="determinate" value={progress} sx={{ mb: 3, height: 4, borderRadius: 1 }} />

      <Stepper activeStep={currentStepIndex} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((step, idx) => (
          <Step key={step.id} completed={idx < currentStepIndex}>
            <StepLabel>
              <Typography variant="caption" sx={{ fontSize: 10 }}>
                {step.title}
              </Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {children && <Box sx={{ mb: 3 }}>{children}</Box>}

      {footerActions ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          {footerActions.map((action, i) =>
            action.onClick ? (
              <Button key={i} variant="outlined" onClick={action.onClick}>
                {action.label}
              </Button>
            ) : (
              <Button key={i} component="a" href={action.to} variant="contained">
                {action.label}
              </Button>
            )
          )}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          {onBack ? (
            <Button startIcon={<ArrowBack />} onClick={onBack} disabled={isFirstStep || isCompleted}>
              Back
            </Button>
          ) : (
            <Box />
          )}
          <Box sx={{ display: 'flex', gap: 2 }}>
            {!isCompleted && onSaveDraft && (
              <Button startIcon={<Save />} variant="outlined" onClick={onSaveDraft} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Draft'}
              </Button>
            )}
            {!isLastStep && !isCompleted && onNext && (
              <Button startIcon={<ArrowForward />} variant="contained" onClick={onNext}>
                Next
              </Button>
            )}
            {isLastStep && !isCompleted && onComplete && (
              <Button startIcon={<Check />} variant="contained" color="success" onClick={onComplete} disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Complete'}
              </Button>
            )}
            {isCompleted && (onDone || onReset) && (
              <>
                {onDone && (
                  <Button variant="contained" onClick={onDone}>
                    Done
                  </Button>
                )}
                {onReset && (
                  <Button variant="outlined" onClick={onReset}>
                    Start Another
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};